import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import { SessionContext, type SessionContextValue } from './session-context';
import { createSessionController, type UnauthorizedBridge } from './session';
import { useApi } from './use-api';

interface SessionProviderProps {
  readonly children: ReactNode;
  readonly unauthorizedBridge?: UnauthorizedBridge;
}

export function SessionProvider({ children, unauthorizedBridge }: SessionProviderProps) {
  const { auth } = useApi();
  const controller = useMemo(
    () => createSessionController(auth, unauthorizedBridge),
    [auth, unauthorizedBridge],
  );
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );

  useEffect(() => {
    const unsubscribe = unauthorizedBridge?.subscribe(controller.handleUnauthorized);
    void controller.bootstrap();
    return unsubscribe;
  }, [controller, unauthorizedBridge]);

  const value = useMemo<SessionContextValue>(
    () => ({
      ...snapshot,
      login: controller.login,
      logout: controller.logout,
      retry: controller.retry,
    }),
    [controller, snapshot],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
